import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Gates any route to authenticated users. Renders a small spinner while
 * the AuthContext is still resolving the current session, redirects to
 * /login otherwise. Preserves the attempted URL in `?next=` so the login
 * flow can send the user back after signing in.
 *
 * Usage:
 *   <Route path="/book-lab/editor" element={<RequireAuth><EditorWorkspace /></RequireAuth>} />
 */
export default function RequireAuth({ children, fallback = "/login" }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div data-testid="require-auth-loading"
           style={{ minHeight: "60vh", display: "grid", placeItems: "center",
                    background: "rgb(6,20,20)", color: "#9AA5A0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
          <Loader2 className="h-4 w-4" style={{ animation: "spin 1s linear infinite" }} />
          Loading…
        </div>
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${fallback}?next=${next}`} replace />;
  }

  return children;
}
